import { useMemo } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./select";
import { Input } from "./input";
import { cn } from "../../lib/utils";

/**
 * Time Picker component that respects snap intervals
 * Allows both typing and dropdown selection
 */
export const TimePicker = ({
  value = "09:00",
  onChange,
  minTime,
  maxTime,
  snapInterval = 15,
  className,
  disabled = false,
  "data-testid": testId
}) => {
  // Parse current value
  const [currentHour, currentMinute] = useMemo(() => {
    const [h, m] = (value || "09:00").split(':').map(Number);
    return [h, m];
  }, [value]);

  // Generate hours options (0-23)
  const hours = useMemo(() => {
    const minHour = minTime ? parseInt(minTime.split(':')[0]) : 0;
    const maxHour = maxTime ? parseInt(maxTime.split(':')[0]) : 23;
    const result = [];
    for (let h = minHour; h <= maxHour; h++) {
      result.push(h);
    }
    return result;
  }, [minTime, maxTime]);

  // Generate minutes options based on snap interval
  const minutes = useMemo(() => {
    const result = [];
    for (let m = 0; m < 60; m += snapInterval) {
      result.push(m);
    }
    return result;
  }, [snapInterval]);

  // Handle hour change
  const handleHourChange = (newHour) => {
    const h = parseInt(newHour);
    // Snap minute to nearest interval
    const snappedMinute = Math.round(currentMinute / snapInterval) * snapInterval % 60;
    const newTime = `${h.toString().padStart(2, '0')}:${snappedMinute.toString().padStart(2, '0')}`;
    onChange?.(newTime);
  };

  // Handle minute change
  const handleMinuteChange = (newMinute) => {
    const m = parseInt(newMinute);
    const newTime = `${currentHour.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
    onChange?.(newTime);
  };

  // Handle direct input (typing)
  const handleInputChange = (e) => {
    const inputValue = e.target.value;
    if (!inputValue) return;
    
    const [h, m] = inputValue.split(':').map(Number);
    if (isNaN(h) || isNaN(m)) return;
    
    // Snap to nearest interval
    const snappedMinute = Math.round(m / snapInterval) * snapInterval % 60;
    const newTime = `${h.toString().padStart(2, '0')}:${snappedMinute.toString().padStart(2, '0')}`;
    onChange?.(newTime);
  };

  return (
    <div className={cn("flex items-center gap-1", className)} data-testid={testId}>
      {/* Hour Select */}
      <Select
        value={currentHour.toString()}
        onValueChange={handleHourChange}
        disabled={disabled}
      >
        <SelectTrigger className="w-[70px] tabular-nums" data-testid={testId ? `${testId}-hour` : undefined}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {hours.map((h) => (
            <SelectItem key={h} value={h.toString()} className="tabular-nums">
              {h.toString().padStart(2, '0')}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <span className="text-muted-foreground tabular-nums">:</span>

      {/* Minute Select */}
      <Select
        value={currentMinute.toString()}
        onValueChange={handleMinuteChange}
        disabled={disabled}
      >
        <SelectTrigger className="w-[70px] tabular-nums" data-testid={testId ? `${testId}-minute` : undefined}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {minutes.map((m) => (
            <SelectItem key={m} value={m.toString()} className="tabular-nums">
              {m.toString().padStart(2, '0')}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* Hidden input for keyboard accessibility and direct typing */}
      <Input
        type="time"
        value={value}
        onChange={handleInputChange}
        step={snapInterval * 60}
        className="sr-only"
        tabIndex={-1}
        aria-hidden="true"
      />
    </div>
  );
};

export default TimePicker;
